// src/hooks/useAdminAuth.ts
import { useState, useEffect, useMemo } from "react";
import { supabase } from "../services/supabaseClient";
import { checkUserPermissions, MemberExportData } from "../services/supabaseAdminOptimized";
import type {
    AdminUser,
    DashboardStats,
    ExportFilters,
    MemberData,
    MemberSearchFilters,
    UseAdminAuthReturn,
    UseDashboardDataReturn,
    UseDataExportReturn,
    UseMemberManagementReturn
} from "../types/admin.types";

// État global singleton pour éviter les re-initialisations
class GlobalAuthState {
    private static instance: GlobalAuthState;
    private user: AdminUser | null = null;
    private loading: boolean = true;
    private isInitialized: boolean = false;
    private listeners: Set<() => void> = new Set();
    private adminCache: Record<string, { isAuthorized: boolean; role: string; userData: any }> = {};

    static getInstance(): GlobalAuthState {
        if (!GlobalAuthState.instance) {
            GlobalAuthState.instance = new GlobalAuthState();
        }
        return GlobalAuthState.instance;
    }

    getState() {
        return { user: this.user, loading: this.loading };
    }

    setState(user: AdminUser | null, loading: boolean) {
        this.user = user;
        this.loading = loading;
        this.notifyListeners();
    }

    subscribe(callback: () => void) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    private notifyListeners() {
        this.listeners.forEach(callback => callback());
    }

    // Fonction modifiée pour vérifier les utilisateurs autorisés (admin ET viewer)
    async checkAuthorizedUser(email: string): Promise<{ isAuthorized: boolean; role: string; userData: any }> {
        if (this.adminCache[email] !== undefined) {
            return this.adminCache[email];
        }

        try {
            console.log('🔍 Vérification autorisation pour:', email);

            const { data, error } = await supabase
                .from('admin_users')
                .select('*')
                .eq('email', email)
                .single();

            if (error) {
                console.log('❌ Utilisateur non trouvé dans admin_users:', error.message);
                this.adminCache[email] = { isAuthorized: false, role: '', userData: null };
                return this.adminCache[email];
            }

            // Vérifier que l'utilisateur est actif et a un rôle valide
            const isAuthorized = data &&
                data.is_active !== false &&
                (data.role === 'admin' || data.role === 'viewer');

            console.log('✅ Utilisateur trouvé:', {
                email: data.email,
                role: data.role,
                is_active: data.is_active,
                isAuthorized
            });

            const result = {
                isAuthorized,
                role: data.role || '',
                userData: data
            };

            this.adminCache[email] = result;
            return result;
        } catch (error) {
            console.error('❌ Erreur lors de la vérification:', error);
            this.adminCache[email] = { isAuthorized: false, role: '', userData: null };
            return this.adminCache[email];
        }
    }

    // Fonction pour récupérer l'utilisateur depuis le localStorage
    private tryRestoreFromLocalStorage(): AdminUser | null {
        try {
            const stored = localStorage.getItem('udb_admin_user');
            if (!stored) return null;

            const parsed = JSON.parse(stored);
            const now = Date.now();
            const maxAge = 24 * 60 * 60 * 1000; // 24 heures

            // Vérifier si les données ne sont pas trop anciennes
            if (parsed.timestamp && (now - parsed.timestamp) > maxAge) {
                localStorage.removeItem('udb_admin_user');
                return null;
            }

            console.log('🔄 Restauration depuis localStorage:', parsed.user.email);
            return parsed.user;
        } catch (error) {
            console.warn('Erreur restauration localStorage:', error);
            try {
                localStorage.removeItem('udb_admin_user');
            } catch (e) {
                // Ignore
            }
            return null;
        }
    }

    async initialize() {
        if (this.isInitialized) {
            return;
        }

        console.log('🚀 Initialisation du système d\'authentification...');
        this.isInitialized = true;

        try {
            // Essayer de restaurer depuis localStorage en premier
            const restoredUser = this.tryRestoreFromLocalStorage();
            if (restoredUser) {
                console.log('✅ Utilisateur restauré depuis localStorage');
                this.setState(restoredUser, false);
                // Continuer la vérification en arrière-plan
            }

            // Attendre un peu pour que Supabase initialise complètement
            await new Promise(resolve => setTimeout(resolve, 100));

            // Vérifier la session actuelle avec retry
            let session = null;
            let attempts = 0;
            const maxAttempts = 3;

            while (!session && attempts < maxAttempts) {
                try {
                    const { data, error } = await supabase.auth.getSession();
                    if (error) {
                        throw error;
                    }
                    session = data.session;
                    break;
                } catch (error) {
                    attempts++;
                    console.warn(`Tentative ${attempts} échouée:`, error);
                    if (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 500));
                    }
                }
            }

            if (session?.user) {
                console.log('📋 Session trouvée, validation de l\'utilisateur...', {
                    userId: session.user.id,
                    email: session.user.email,
                    expiresAt: session.expires_at
                });
                await this.validateAndSetUser(session.user);
            } else {
                console.log('📋 Aucune session trouvée après', attempts, 'tentatives');
                // Si on n'a pas de session et pas d'utilisateur restauré, déconnecter
                if (!restoredUser) {
                    this.setState(null, false);
                } else {
                    // Garder l'utilisateur restauré mais marquer comme non vérifié
                    console.log('⚠️ Session expirée mais utilisateur restauré');
                }
            }

            // Écouter les changements d'auth
            supabase.auth.onAuthStateChange(async (event, session) => {
                console.log('🔄 Changement d\'état auth:', event);

                switch (event) {
                    case 'SIGNED_IN':
                        if (session?.user) {
                            console.log('✅ Connexion détectée');
                            this.setState(null, true); // Loading
                            await this.validateAndSetUser(session.user);
                        }
                        break;

                    case 'SIGNED_OUT':
                        console.log('🚪 Déconnexion détectée');
                        this.adminCache = {};
                        this.setState(null, false);
                        break;

                    case 'TOKEN_REFRESHED':
                        console.log('🔄 Token rafraîchi');
                        // Maintenir l'état actuel
                        break;

                    default:
                        break;
                }
            });

        } catch (error) {
            console.error('❌ Erreur initialisation auth:', error);
            this.setState(null, false);
        }
    }

    private async validateAndSetUser(authUser: any) {
        try {
            console.log('🔍 Validation de l\'utilisateur:', {
                id: authUser.id,
                email: authUser.email,
                lastSignIn: authUser.last_sign_in_at
            });

            const { isAuthorized, role, userData } = await this.checkAuthorizedUser(authUser.email || '');

            if (isAuthorized && userData) {
                console.log('✅ Utilisateur autorisé avec le rôle:', role);

                // Créer l'objet AdminUser pour tous les rôles autorisés
                const adminUser: AdminUser = {
                    id: authUser.id,
                    email: authUser.email || '',
                    role: userData.role,
                    name: userData.name || authUser.user_metadata?.name || authUser.email?.split('@')[0] || 'Utilisateur',
                    is_active: userData.is_active ?? true,
                    // Permissions basées sur le rôle
                    can_read: userData.can_read ?? true, // Tous les utilisateurs autorisés peuvent lire
                    can_write: userData.can_write ?? (userData.role === 'admin'),
                    can_delete: userData.can_delete ?? (userData.role === 'admin'),
                    can_manage_users: userData.can_manage_users ?? (userData.role === 'admin'),
                    created_at: userData.created_at || new Date().toISOString()
                };

                console.log('👤 Utilisateur créé:', {
                    email: adminUser.email,
                    role: adminUser.role,
                    permissions: {
                        read: adminUser.can_read,
                        write: adminUser.can_write,
                        delete: adminUser.can_delete,
                        manage_users: adminUser.can_manage_users
                    }
                });

                // Sauvegarder dans le localStorage pour la persistance
                try {
                    localStorage.setItem('udb_admin_user', JSON.stringify({
                        user: adminUser,
                        timestamp: Date.now()
                    }));
                } catch (e) {
                    console.warn('Impossible de sauvegarder dans localStorage:', e);
                }

                this.setState(adminUser, false);
            } else {
                console.log('❌ Utilisateur non autorisé');
                // Nettoyer le localStorage
                try {
                    localStorage.removeItem('udb_admin_user');
                } catch (e) {
                    console.warn('Erreur nettoyage localStorage:', e);
                }
                this.setState(null, false);
            }
        } catch (error) {
            console.error('❌ Erreur validation utilisateur:', error);
            // Nettoyer le localStorage en cas d'erreur
            try {
                localStorage.removeItem('udb_admin_user');
            } catch (e) {
                console.warn('Erreur nettoyage localStorage:', e);
            }
            this.setState(null, false);
        }
    }

    clearCache() {
        this.adminCache = {};
    }
}

const globalAuthState = GlobalAuthState.getInstance();

// Hook principal pour l'authentification admin
export const useAdminAuth = (): UseAdminAuthReturn => {
    const [state, setState] = useState(() => globalAuthState.getState());

    useEffect(() => {
        // S'abonner aux changements d'état global
        const unsubscribe = globalAuthState.subscribe(() => {
            setState(globalAuthState.getState());
        });

        // Initialiser si pas encore fait
        globalAuthState.initialize();

        // Retourner la fonction de nettoyage
        return () => {
            unsubscribe();
        };
    }, []);

    return { user: state.user, loading: state.loading };
};

// Hook pour les permissions avec cache
export const usePermissions = () => {
    const { user } = useAdminAuth();

    return useMemo(() => checkUserPermissions(user), [user]);
};

// Hook pour les actions qui nécessitent des permissions spécifiques
export const useAdminActions = () => {
    const permissions = usePermissions();

    return useMemo(() => ({
        // Actions de lecture (disponibles pour tous les admins)
        canViewDashboard: permissions.canRead,
        canViewMembers: permissions.canRead,
        canViewStats: permissions.canRead,
        canExportData: permissions.canRead,

        // Actions d'écriture (admin uniquement)
        canEditMembers: permissions.canWrite,
        canCreateContent: permissions.canWrite,
        canSendEmails: permissions.canWrite,

        // Actions de suppression (admin uniquement)
        canDeleteMembers: permissions.canDelete,
        canDeleteContent: permissions.canDelete,

        // Gestion des utilisateurs (admin uniquement)
        canManageAdminUsers: permissions.canManageUsers,
        canChangePermissions: permissions.canManageUsers,

        // Informations sur l'utilisateur
        userRole: permissions.user?.role,
        isAdmin: permissions.isAdmin,
        isViewer: permissions.isViewer,
        hasAccess: permissions.hasAccess
    }), [permissions]);
};

// Hook pour vérifier une permission spécifique
export const useHasPermission = (permission: 'read' | 'write' | 'delete' | 'manage_users') => {
    const permissions = usePermissions();

    return useMemo(() => {
        const permissionMap = {
            read: permissions.canRead,
            write: permissions.canWrite,
            delete: permissions.canDelete,
            manage_users: permissions.canManageUsers
        };
        return permissionMap[permission];
    }, [permissions, permission]);
};

// Hook pour les données du dashboard avec gestion d'état
export const useDashboardData = (): UseDashboardDataReturn => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    const permissions = usePermissions();

    const loadStats = async (useCache = true) => {
        if (!permissions.canRead) {
            setError('Permission de lecture requise');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { MemberManagementService } = await import('../services/supabaseAdminOptimized');
            const { data, error: statsError } = await MemberManagementService.getDashboardStats(useCache);

            if (statsError) throw new Error(statsError.message || 'Erreur de chargement');

            setStats(data);
            setLastRefresh(new Date());
        } catch (err: any) {
            setError(err.message || 'Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (permissions.canRead) {
            loadStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissions.canRead]);

    const refresh = () => loadStats(false);

    return {
        stats,
        loading,
        error,
        lastRefresh,
        refresh,
        canRefresh: permissions.canRead
    };
};

// Hook pour la gestion des membres avec pagination
export const useMemberManagement = (): UseMemberManagementReturn => {
    const [members, setMembers] = useState<MemberData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        pageSize: 20
    });

    const permissions = usePermissions();

    const searchMembers = async (filters: MemberSearchFilters = {}) => {
        if (!permissions.canRead) {
            setError('Permission de lecture requise');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { MemberManagementService } = await import('../services/supabaseAdminOptimized');
            const result = await MemberManagementService.searchMembers(
                filters.searchQuery,
                filters.province,
                filters.status,
                filters.page || 1,
                filters.pageSize || 20
            );

            if (result.error) {
                throw new Error(result.error.message || 'Erreur de recherche');
            }

            setMembers(result.data);
            setPagination({
                currentPage: result.currentPage,
                totalPages: result.totalPages,
                totalCount: result.count,
                pageSize: filters.pageSize || 20
            });
        } catch (err: any) {
            setError(err.message || 'Erreur lors de la recherche de membres');
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    const updateMember = async (id: string, updates: Partial<MemberData>) => {
        if (!permissions.canWrite) {
            throw new Error('Permission d\'écriture requise');
        }

        const { MemberManagementService } = await import('../services/supabaseAdminOptimized');
        const result = await MemberManagementService.updateMember(id, updates);

        if (result.error) {
            throw new Error(result.error.message || 'Erreur de mise à jour');
        }

        // Rafraîchir la liste
        await searchMembers({ page: pagination.currentPage });

        return result;
    };

    const deleteMember = async (id: string) => {
        if (!permissions.canDelete) {
            throw new Error('Permission de suppression requise');
        }

        const { MemberManagementService } = await import('../services/supabaseAdminOptimized');
        const result = await MemberManagementService.deleteMember(id);

        if (result.error) {
            throw new Error(result.error.message || 'Erreur de suppression');
        }

        // Rafraîchir la liste
        await searchMembers({ page: pagination.currentPage });

        return result;
    };

    return {
        members,
        loading,
        error,
        pagination,
        searchMembers,
        updateMember: permissions.canWrite ? updateMember : null,
        deleteMember: permissions.canDelete ? deleteMember : null,
        canEdit: permissions.canWrite,
        canDelete: permissions.canDelete
    };
};

// Hook pour l'export de données
export const useDataExport = (): UseDataExportReturn => {
    const [exporting, setExporting] = useState(false);
    const permissions = usePermissions();

    const exportMembers = async (filters: ExportFilters = {}) => {
        if (!permissions.canRead) {
            throw new Error('Permission de lecture requise pour l\'export');
        }

        setExporting(true);

        try {
            const { MemberManagementService } = await import('../services/supabaseAdminOptimized');
            const { data, error } = await MemberManagementService.exportMembers(filters);

            if (error) {
                throw new Error(error.message || 'Erreur d\'export');
            }

            if (!data || data.length === 0) {
                throw new Error('Aucune donnée à exporter');
            }

            // Créer et télécharger le CSV
            const headers = [
                'Numéro', 'Prénom', 'Nom', 'Email', 'Téléphone',
                'Province', 'Ville', 'Profession', 'Sexe',
                'Parrainages', 'Statut', 'Date adhésion'
            ];

            const csvContent = [
                headers.join(','),
                ...data.map((member: MemberExportData) => [
                    member.member_number,
                    member.first_name,
                    member.last_name,
                    member.email || '',
                    member.phone,
                    member.province,
                    member.city,
                    member.profession || '',
                    member.gender || '',
                    member.referrals_count,
                    member.status,
                    new Date(member.created_at).toLocaleDateString('fr-FR')
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `membres_udb_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            return { success: true, count: data.length };
        } catch (error: any) {
            throw error;
        } finally {
            setExporting(false);
        }
    };

    return {
        exportMembers: permissions.canRead ? exportMembers : null,
        exporting,
        canExport: permissions.canRead
    };
};

const adminAuthHooks = {
    useAdminAuth,
    usePermissions,
    useAdminActions,
    useHasPermission,
    useDashboardData,
    useMemberManagement,
    useDataExport
};

export default adminAuthHooks;
